import { CheckCircleOutlined } from "@mui/icons-material";
import { Alert, CircularProgress, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React, { useMemo } from "react";
import { isBasketFulfillmentComplete } from "../../api/funds";
import { useBasketStrings } from "./basket-strings";
import { useBasketContext } from "./BasketProvider";

/**
 * Payment result dialog (design "State 2" + success state):
 * shown after checkout while orders register, on full success, and when
 * Viva succeeded but registration failed/partially failed (with Retry).
 */
export default function BasketPaymentResult() {
    const strings = useBasketStrings();
    const { fulfillment, retryFulfillment, dismissFulfillment, openBasket } = useBasketContext();

    const open = fulfillment != null;
    const status = fulfillment?.status;
    const paid = fulfillment?.paid ?? false;
    const result = fulfillment?.result;

    const processing = status === 'processing';
    const allCommitted = status === 'done' && isBasketFulfillmentComplete(result, paid);
    const committedCount = result?.days?.filter(d => d.committed).length ?? 0;
    const totalCount = result?.days?.length ?? 0;
    const partial = status === 'done' && !allCommitted && committedCount > 0;
    const noSessionWithoutPayment = status === 'done' && !!result?.sessionAbsent && !paid;

    const showSuccessHeader = (paid || allCommitted) && !noSessionWithoutPayment;

    const dialogTitle = useMemo(() => {
        if (!open) return '';
        if (processing) return strings.registering;
        if (showSuccessHeader) {
            return allCommitted ? strings.ordersRegisteredTitle : strings.paymentSuccessTitle;
        }
        if (noSessionWithoutPayment) return strings.checkoutFailedTryAgain;
        return strings.registrationFailed;
    }, [open, processing, showSuccessHeader, allCommitted, noSessionWithoutPayment, strings]);

    const dialogSubtitle = showSuccessHeader && !processing
        ? (allCommitted ? strings.ordersRegisteredSubtitle : strings.paymentSuccessSubtitle)
        : null;

    const alertMessage = !processing && !allCommitted
        ? (noSessionWithoutPayment
            ? strings.checkoutFailedTryAgain
            : partial
                ? `${strings.partialRegisteredPrefix}${committedCount}${strings.partialRegisteredMiddle}${totalCount}${strings.partialRegisteredSuffix}`
                : strings.registrationFailed)
        : null;

    const dialogDescribedBy = useMemo(() => {
        const ids: string[] = [];
        if (dialogSubtitle) ids.push('basket-payment-dialog-desc');
        if (alertMessage && !allCommitted) ids.push('basket-payment-dialog-alert');
        return ids.length ? ids.join(' ') : undefined;
    }, [dialogSubtitle, alertMessage, allCommitted]);

    const onDismiss = () => {
        void dismissFulfillment().then(() => {
            if (!allCommitted) {
                // Remaining days are still in the basket — bring the user back to it
                openBasket();
            }
        });
    };

    const onRetry = () => {
        // Credits-only: re-quotes then registers; paid: wallet already credited — register only
        void retryFulfillment();
    };

    const handleDialogClose = () => {
        if (processing) return;
        onDismiss();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            maxWidth="xs"
            fullWidth
            aria-labelledby={processing || showSuccessHeader ? 'basket-payment-dialog-title' : undefined}
            aria-label={!processing && !showSuccessHeader ? dialogTitle : undefined}
            aria-describedby={dialogDescribedBy}
            slotProps={{
                paper: { className: 'basket-payment-card' },
                backdrop: { className: 'basket-payment-dialog-backdrop' },
            }}
        >
            {processing && (
                <DialogContent className="basket-payment-dialog-content">
                    <CircularProgress sx={{ color: 'var(--lb_green)' }} aria-hidden />
                    <DialogTitle id="basket-payment-dialog-title" className="basket-payment-subtitle basket-payment-dialog-title-only">
                        {strings.registering}
                    </DialogTitle>
                </DialogContent>
            )}

            {!processing && (
                <DialogContent className="basket-payment-dialog-content">
                    {showSuccessHeader && (
                        <>
                            <CheckCircleOutlined className="basket-payment-icon" sx={{ fontSize: 64, color: 'var(--lb_green)' }} aria-hidden />
                            <DialogTitle id="basket-payment-dialog-title" className="basket-payment-title basket-payment-dialog-title-only">
                                {dialogTitle}
                            </DialogTitle>
                            {dialogSubtitle && (
                                <p id="basket-payment-dialog-desc" className="basket-payment-subtitle">
                                    {dialogSubtitle}
                                </p>
                            )}
                        </>
                    )}

                    {!allCommitted && alertMessage && (
                        <Alert
                            id="basket-payment-dialog-alert"
                            severity="warning"
                            className="basket-payment-alert"
                        >
                            {alertMessage}
                        </Alert>
                    )}

                    <div className="basket-payment-actions">
                        {!allCommitted && (
                            <button type="button" className="order-completed-button2 basket-payment-retry" onClick={onRetry}>
                                {strings.retryRegistration}
                            </button>
                        )}
                        <button
                            type="button"
                            className={allCommitted ? 'order-completed-button2' : 'hollow-button'}
                            onClick={onDismiss}
                        >
                            {strings.close}
                        </button>
                    </div>
                </DialogContent>
            )}
        </Dialog>
    );
}
